/**
 * Splitting an upload into one Submission per student — the WORK, addressable by ids so a job can run it.
 *
 * WHY IT MOVED OUT OF `file-upload.ts`. This was the last LLM pipeline still running inline in a request, and
 * the only part of setup the job system did not cover. A restart mid-split left the exam with `uploadProgress`
 * initialised, `splitConfirmationProgress` STARTED and no submissions — and because nothing derives the split
 * from exam state, nothing ever picked it up again. The teacher had to re-upload with no indication why.
 *
 * The blocker named in `jobs/types/index.ts` was that `runSplitFileAgentMutation` takes `File[]`, which is not
 * serialisable into a payload. `resolveFileIdsToFiles` already reconstructs those objects from stored PDF and
 * image documents, server-side, from nothing but ids — so the payload is ids and this function does the
 * reconstruction itself. Every caller now hands over ids and the work happens under a lease.
 *
 * IT IS THE `heavy` POOL'S FIRST MEMBER, and it is the reason that pool exists. The split agent's LLM call is
 * await-bound and would belong in `io`, but what follows it is not: every student block goes through
 * `persistFileAsImageIds`, which explodes a PDF into page images and encodes each one. That is core-bound work
 * in the encode worker, exactly what `cpuHeadroomCheck` refuses to admit against a saturated event loop, and
 * with two uploads in flight it is what would starve everything else on the instance.
 *
 * RETRIES ARE SAFE BECAUSE THE ROWS ARE TAGGED. A submission carries the `splitJobId` that created it, and the
 * first thing a run does is deal with its own previous attempt's submissions. Without that, an attempt that died
 * after creating six of ten students would leave those six behind and the retry would produce sixteen — the
 * one failure mode that is worse than not retrying at all, because it is silent and the teacher would grade
 * the duplicates.
 *
 * DEAL WITH, not delete, and the difference is the teacher's work: a cohort the teacher has already CONFIRMED is
 * kept and reported as-is, because by then the submissions are what everything downstream is working from. Only an
 * unconfirmed one is rebuilt. See the guard at the top of `runSubmissionSplit`.
 */

import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import i18next from 'i18next';
import '../../i18n.js';
import {
  ConfirmationProgressStatus,
  incrementSplitConfirmationFinishedByExam,
  setSplitConfirmationProgressByExam,
  setSplitConfirmationTotalByExam,
} from '../confirmation-progress.js';
import { Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';
import { resolveIsStudentOwnedExam } from '../utils/exam-role.js';
import type { EvaluatedStudentBlock } from './agents/calculate-split-confidence.js';
import { runSplitFileAgentMutation } from './agents/split-file-agent.js';
import { confirmSplitForExam } from './confirm-submission-split.js';
import {
  createSubmissionSplitConfirmationMutation,
  formatSplitIssueDetails,
} from './create-submission-split-confirmation.js';
import { createSubmissionMutation } from './create-submission.js';
import { persistFileAsImageIds, resolveFileIdsToFiles, uploadLogPrefix } from './file-intake.js';
import { startStructureExtractionMutation } from './start-structure-extraction.js';
import { structureExtractionStarted } from './tools/file-assignment.js';
import { updateSubmissionsFoundMutation, updateUploadProgressMutation } from './update-exam-state.js';
import { UserFacingUploadError } from './upload-errors.js';
import { resolveConversationLanguage } from './utils/system-info.js';

export interface SubmissionSplitRequest {
  /** Already-persisted uploads to split. Ids, so this survives being carried in a job payload. */
  fileIds: string[];
  /** Where the confirmation request opens. Without one there is nobody to ask, so the split still runs. */
  conversationId?: string;
  guestUserID?: string;
  /** Who the submissions belong to, resolved at dispatch. See the payload field of the same name. */
  ownerUserID?: string;
  /** The row doing this work. Tags the submissions it creates so its own retry can clean up after itself. */
  splitJobId: string;
}

/**
 * Waits for the conversation to stop thinking before the confirmation request is posted.
 *
 * Kept because the confirmation is a MESSAGE: posting it while the agent is mid-turn interleaves it with the
 * agent's own output. Time-boxed and non-fatal — a conversation that never settles must not cost the teacher
 * the confirmation, only its placement.
 */
export async function waitForConversationFinishedThinking(conversationId: string, timeoutMs = 120_000): Promise<void> {
  const start = Date.now();
  const conversationCollection = await getCollection(Conversation);

  while (Date.now() - start < timeoutMs) {
    const conversation = await conversationCollection.findOne({ _id: conversationId as any });
    if (!conversation || conversation.thinking === false) return;
    await new Promise<void>(resolve => {
      setTimeout(resolve, 300);
    });
  }

  logger.warn({ timeoutMs, conversationId }, 'Timeout waiting for conversation to finish thinking. Proceeding anyway.');
}

/**
 * What a previous attempt of THIS row left behind, and whether any of it is already in use.
 *
 * Scoped by `splitJobId`, never by exam: a re-upload's split must not touch the submissions of the round before
 * it, and those are the overwhelming majority of what an exam holds.
 */
async function previousAttempt(examId: string, splitJobId: string): Promise<{ ids: string[]; confirmed: boolean }> {
  const submissionCollection = await getCollection(Submission);
  const existing = await submissionCollection
    .find({ examId, splitJobId }, { projection: { _id: 1, splitConfirmed: 1 } })
    .toArray();

  return {
    ids: existing.map(submission => String((submission as any)._id)),
    confirmed: existing.some(submission => (submission as any).splitConfirmed === true),
  };
}

/**
 * Removes the submissions a previous attempt of THIS row created, so the run below starts from nothing.
 *
 * ONLY EVER CALLED FOR AN UNCONFIRMED COHORT — see `runSubmissionSplit`. Deleting one the teacher has confirmed
 * destroys work nothing can reconstruct.
 */
async function clearPreviousAttempt(examId: string, splitJobId: string): Promise<void> {
  // Raw, because a delete is not something the entity collection exposes — see `job-store.ts`, same reason.
  const raw = await getRawCollection<any>(Submission._collectionName);
  const result = await raw.deleteMany({ examId, splitJobId } as any);
  if (result.deletedCount > 0) {
    logger.warn({ examId, splitJobId, removed: result.deletedCount }, "Removed a previous split attempt's submissions");
  }
}

/**
 * Runs the split and creates one Submission per student block. Returns the submission ids it created.
 *
 * Throws on a split-agent failure, and that is what the row's retry is for. The upload counter is given back
 * first so a failed attempt does not leave the bar claiming files that produced nothing.
 */
export async function runSubmissionSplit(examId: string, request: SubmissionSplitRequest): Promise<string[]> {
  const { fileIds, conversationId, guestUserID, ownerUserID, splitJobId } = request;
  if (!examId || fileIds.length === 0) return [];

  // A CONFIRMED COHORT ENDS THE RETRY, and this guard is the difference between retry-safety and data loss. The
  // confirmation request is the LAST thing this function posts, so it is live while the row is still `claimed` —
  // and a row is handed back to the queue on every deploy (`requeueJob(..., 'instance-restarted')`), not only on a
  // crash. In that window the teacher can confirm the split and correct student names, and the delete below would
  // then take the confirmation, the names, and any extracted answers with it, silently: answer extraction just
  // reports `submission no longer exists` and the fresh cohort comes back with new ids and unconfirmed.
  //
  // If they confirmed, this row's work is done by definition — the submissions exist and everything downstream
  // already has them — so the honest outcome is to report what is there rather than to rebuild it.
  const previous = await previousAttempt(examId, splitJobId);
  if (previous.confirmed) {
    logger.warn(
      { examId, splitJobId, submissions: previous.ids.length },
      'A previous attempt of this split is already confirmed — keeping it instead of re-splitting',
    );
    return previous.ids;
  }

  await clearPreviousAttempt(examId, splitJobId);

  // A student only ever uploads their own single exam — nothing to confirm, since there's no
  // possibility of a wrong page-to-student assignment. See resolveIsStudentOwnedExam.
  const isStudentSelfUpload = await resolveIsStudentOwnedExam(examId);

  const sourceFiles = await resolveFileIdsToFiles(fileIds);
  if (sourceFiles.length === 0) {
    logger.error({ examId, fileIds }, 'Split has no resolvable source files');
    return [];
  }

  // NOTHING IS DECLARED HERE ANY MORE. This used to compute `sourceBytes × BYTES_PER_SOURCE_BYTE` and hand it to
  // `declareHeap`, so admission could see the upload's real footprint instead of the type's worst case — and it was
  // the only handler in the system that did. It went with the reservation system (see `jobs/load.ts`), and the
  // estimate was measuring the wrong resource anyway: the peak here is page rasterisation, whose buffers are
  // `external` memory that the V8 heap budget never governed.
  //
  // What bounds this work now is `PDF_EXTRACT_CONCURRENCY` in `extractImageFromPDF` — four pages at a time,
  // enforced where the pages are actually allocated rather than guessed at from the file size.

  await updateUploadProgressMutation(examId, sourceFiles.length)();
  await setSplitConfirmationProgressByExam(examId, {
    status: ConfirmationProgressStatus.STARTED,
    confirmed: false,
    message: 'progress.analyzingSplit',
  });

  logger.info({ examId, fileCount: sourceFiles.length }, 'Running split agent for exam');
  const splitResult = await runSplitFileAgentMutation(sourceFiles, examId);

  const splitFailureDetected = Array.isArray(splitResult.issueDetails)
    ? splitResult.issueDetails.some(detail => detail.code === 'split_agent_failure')
    : false;
  if (splitFailureDetected) {
    // Thrown rather than swallowed: the row fails, keeps its remaining attempts, and the sweeper or the next
    // tick runs it again. The inline version reported this to the teacher as final.
    const lng = (await resolveConversationLanguage({ examId })) || 'de';
    const failureMessage = i18next.t('splitIssues.splitAgentFailed', {
      lng,
      defaultValue:
        'Die automatische Zuordnung der Schülerabgaben ist fehlgeschlagen. Bitte versuche den Upload später erneut.',
    });
    throw new UserFacingUploadError('SPLIT_AGENT_FAILURE', failureMessage, failureMessage);
  }

  const files = splitResult.files;
  const studentNames = Array.isArray(splitResult.studentNames) ? splitResult.studentNames : [];
  const blocks: EvaluatedStudentBlock[] = Array.isArray(splitResult.studentBlocks) ? splitResult.studentBlocks : [];

  await setSplitConfirmationTotalByExam(examId, files.length);
  await updateSubmissionsFoundMutation(examId, files.length)();

  const submissionIds: string[] = [];
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    if (!file) continue;

    // THE HEAVY HALF, and the peak of this whole job: a PDF block becomes page images and each is encoded — the
    // reason this type is `heavy`. Sequential, one block at a time, which is what lets `splitEstimate` above cover
    // it with one standing figure instead of a per-block scope.
    const imageIds = await persistFileAsImageIds(file, uploadLogPrefix('submissions'));
    if (!imageIds) {
      logger.error({ examId, index, fileName: (file as any)?.name }, 'Split block produced no images — skipping');
      continue;
    }

    const studentName =
      studentNames[index] ??
      i18next.t('upload.studentName', { index: index + 1, defaultValue: `Schüler ${index + 1}` });
    const block = blocks[index];
    const submission = await createSubmissionMutation(
      examId,
      imageIds,
      studentName,
      block?.final_confidence,
      block?.block_issues,
      // The owner slot. `createSubmissionMutation` prefers a resolved session and falls back to this — and in a
      // job there IS no session, so this is the only thing standing between the cohort and `userID: null`.
      ownerUserID ?? guestUserID,
      splitJobId,
    )({ _id: 1 });
    submissionIds.push(submission._id);
    await incrementSplitConfirmationFinishedByExam(examId);
  }

  // AFTER the loop, so `loadExtractionContext` finds the complete submission set when the teacher confirms.
  if (isStudentSelfUpload) {
    // Auto-confirm instead of posting a [[SPLIT_CONFIRMATION]] message and waiting on a human —
    // mirrors exactly what confirmSubmissionSplitMutation does for a real teacher confirmation.
    // total/finished are passed explicitly rather than relying on setSplitConfirmationProgressByExam's
    // read-then-merge of the existing state, since that state was written moments ago by the loop above.
    await setSplitConfirmationProgressByExam(examId, {
      status: ConfirmationProgressStatus.DONE,
      confirmed: true,
      message: 'progress.assignmentConfirmed',
      total: files.length,
      finished: submissionIds.length,
    });
    await confirmSplitForExam(examId);

    // A student's own upload has no separate blank exam to derive the task structure from — these
    // submissions ARE the structure source. For a teacher this same kickoff normally happens via the
    // confirm_split_in_chat tool result (see agent-loop.ts), which this silent auto-confirm bypasses
    // entirely, so it has to be triggered here instead or the structure step never starts.
    const examCollection = await getCollection(Exam);
    const currentExam = await examCollection.findOne({ _id: examId }, { projection: { examState: 1 } });
    if (!structureExtractionStarted((currentExam as any)?.examState)) {
      await startStructureExtractionMutation(examId, conversationId ?? '')();
    }
  } else if (conversationId) {
    const totalStudents = blocks.length;
    const studentsWithoutName = blocks.filter(block => !block.student_name?.trim()).length;
    const pageCounts = blocks.map(block => (Array.isArray(block.pages) ? block.pages.length : 0));
    const issueDetails = Array.isArray(splitResult.issueDetails)
      ? splitResult.issueDetails
      : splitResult.issues.map(issue => ({
          code: 'split_agent_failure' as const,
          severity: 'error' as const,
          sourceFileName: issue.trim() || undefined,
        }));

    await waitForConversationFinishedThinking(conversationId);
    await createSubmissionSplitConfirmationMutation(
      examId,
      conversationId,
      totalStudents,
      totalStudents,
      studentsWithoutName,
      pageCounts.length > 0 ? Math.min(...pageCounts) : 0,
      pageCounts.length > 0 ? Math.max(...pageCounts) : 0,
      formatSplitIssueDetails(issueDetails, totalStudents, studentsWithoutName, blocks),
      issueDetails.some(detail => detail.severity === 'error'),
      issueDetails.some(detail => detail.severity === 'warning'),
    )();
  }

  logger.info({ examId, created: submissionIds.length, of: files.length }, 'Split created submissions');
  return submissionIds;
}

/**
 * Queues the split from wherever the upload happened — including the BROWSER, which is the only reason this is
 * a mutation rather than a direct call.
 *
 * `handleImageFileUpload` runs on both sides: the client's upload panel imports it (it uses the file picker),
 * and the upload agent calls it server-side. Enqueuing writes to Mongo, so the client cannot do it — and the
 * job system must not be pulled into the client bundle to try. The dispatcher is therefore imported inside the
 * resolver, where only the server ever reaches it.
 */
export const dispatchSubmissionSplitMutation = registerMutation({
  operationName: 'dispatchSubmissionSplitMutation',
  type: () => String,
  params: [
    { name: 'examId', type: () => String },
    { name: 'fileIds', type: () => [String] },
    { name: 'conversationId', type: () => String },
    { name: 'guestUserID', type: () => String },
  ],
  resolve:
    (examId: string, fileIds: string[], conversationId?: string, guestUserID?: string) => async (): Promise<string> => {
      // RESOLVED HERE, not in the handler: this runs in the request, so it is the last place a session exists.
      // A background handler asking the same question gets a throw, which is what failed the first split.
      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 }).catch(() => null);
      const ownerUserID = currentUser?._id || guestUserID || '';

      const { dispatchJobs } = await import('./jobs/dispatch.js');
      const { jobIds } = await dispatchJobs('submissionSplit', examId, {
        fileIds,
        ...(conversationId ? { conversationId } : {}),
        ...(guestUserID ? { guestUserID } : {}),
        ...(ownerUserID ? { ownerUserID } : {}),
      });
      return jobIds[0] ?? '';
    },
});
